import { API, graphqlOperation } from "aws-amplify";
import { trackCarByActiveByTrackBySortKey } from "../graphql/queries";
import {
  createLocation,
  createSeal,
  deleteLocation,
  updateAisle,
  updateLocation,
} from "../graphql/mutations";
import {
  createInventorieId,
  createInventoryItemId,
  updateInventoryItemMini,
} from "../graphql/inventory/custom-mutations";
import {
  getInventorie,
  inventoryByAreaByProductByStatus,
  inventoryByAreaByProductByStatusMax,
  inventoryItemByLocationByStatusLookup,
} from "../graphql/inventory/custom-queries";
import { createInboundShipmentId } from "../graphql/inbound/custom-mutations";
import { getLocationProduct } from "../graphql/account/account-queries";
import { performUpdateInboundMini } from "../graphql/inbound/inboundCRUD";
import { performUpdateInventoryMini } from "../graphql/inventory/inventoryCRUD";

import { logError } from "./appUtils";
import { normalizeValue } from "./utils";
import {
  AreaType,
  LocationLoadType,
  LocationType,
  CarrierType,
  InventoryStatus,
  InboundStatus,
  InboundState,
  NoteType,
  SORTKEY_BUFFER,
  LotType,
} from "./constants";
import {
  getYardCommodity,
  getWeightUOM,
  isBulkCommodity,
  isBulkShipment,
  isShippingContainer,
} from "./loadTypeUtils";
import { canStoreInBulkTank } from "./damagedInventory";

export const isCapacitySliderVisible = (location) => {
  if (!location) return false;
  if (location.area?.areaType === AreaType.TRACK) return false;
  if (location.type === LocationType.BULK) return false;
  if (location.type === LocationType.BULK_MOBILE) return false;
  if (location.type === LocationType.UNSTRUCTURED) return true;
  if (location.locationLoad?.container?.selected) return false;
  if (!location.locationLoad?.other?.selected) return false;
  if (!location.locationLoad) return false; // remove flash on mutate
  return true;
};

export const setInventoryAisleList = (inventory) => {
  const aisleIdMap = {};
  if (inventory.inventoryItems?.items) {
    inventory.inventoryItems.items.forEach((itm) => {
      if (itm.status !== InventoryStatus.SHIPPED) {
        aisleIdMap[itm.aisleId] = true;
      }
    });
  }
  inventory.aisleList = Object.keys(aisleIdMap);
};

export const setCapacityUsed = async (
  location,
  capacityUsed = location.capacityUsed,
  capacityAllocated = location.capacityAllocated,
) => {
  await promiseSetCapacityUsed(location, capacityUsed, capacityAllocated);
};

export const promiseSetCapacityUsed = (
  location,
  capacityUsed = location.capacityUsed,
  capacityAllocated = location.capacityAllocated,
) => {
  const toUpdateLocation = { ...location, capacityUsed, capacityAllocated };

  delete toUpdateLocation.aisle;
  delete toUpdateLocation.area;
  delete toUpdateLocation.createdAt;
  delete toUpdateLocation.locationLoad;
  delete toUpdateLocation.tags;
  delete toUpdateLocation.updatedAt;
  delete toUpdateLocation.zone;
  return API.graphql(
    graphqlOperation(updateLocation, { input: toUpdateLocation }),
  );
};

/** Persists on BULK_MOBILE Location for outbound validation (with inventory.containerNumber / location.containerNumber). */
export const resolveInboundContainerNumber = (inventory, inboundShipment) =>
  (inventory?.containerNumber || inboundShipment?.containerNumber || "").trim();

const getBayNumber = (locationObj) => {
  return `${locationObj.bay < 10 ? "0" : ""}${locationObj.bay}`;
};

export const getAisleFullName = (aisle) => {
  if (!aisle || !aisle?.area) return "";
  return `${aisle.area.prefix}${aisle.zone.prefix}-${aisle.name}`;
};

export const getLocationViewName = (location) => {
  if (!location || !location?.id) return "";
  if (location.type === LocationType.BULK) {
    if (location.name) return location.name;
    return location.position;
  } else if (location.type === LocationType.UNSTRUCTURED) {
    return location.name;
  } else {
    if (location.name) return location.name;
    return `${getBayNumber(location)}${location.position}-${location.level}`;
  }
};

export const getLocationFullName = (location) => {
  if (!location) return "Unspotted";

  if (typeof location === "string" || typeof location === "number") {
    const locationValue = `${location}`.trim();
    if (!locationValue) return "Unspotted";
    if (!locationValue.includes("-") && /^\d+$/.test(locationValue)) {
      return `T-${locationValue}`;
    }
    return locationValue;
  }

  if (!location?.id) return "Unspotted";

  // STAFF LEVEL FIX: Simplified naming for Tracks (e.g., "T-1")
  // Check areaType directly on the location object if joined, or via area relation
  const areaType = location.area?.areaType || location.areaType;
  const prefix = location.area?.prefix || location.prefix || "T";

  if (areaType === AreaType.TRACK) {
    return `${prefix}-${location.name || location.aisle?.name || ""}`;
  }

  if (location?.area && location?.zone && location?.aisle) {
    if (location.type === LocationType.UNSTRUCTURED) {
      return `${location.area.prefix}${location.zone.prefix}-${location.name}`;
    } else if (location.type === LocationType.BULK) {
      if (location.name)
        return `${location.area.prefix}${location.zone.prefix}-${location.aisle?.name}-${location.name}`;
      return `${location.area.prefix}${location.zone.prefix}-${location.aisle?.name}-${location.position}`;
    } else {
      if (location.name)
        return `${location.area.prefix}${location.zone.prefix}-${location.aisle?.name}-${location.name}`;
      return `${location.area.prefix}${location.zone.prefix}-${
        location.aisle?.name
      }-${getBayNumber(location)}${location.position}-${location.level}`;
    }
  } else {
    const storedFull =
      typeof location.locationFullName === "string"
        ? location.locationFullName.trim()
        : "";
    if (storedFull) return storedFull;
    const viewFallback = getLocationViewName(location);
    if (viewFallback) return viewFallback;
    logError(
      "admin:utils:locationUtils:getLocationFullName",
      `Missing area/zone/aisle: ${JSON.stringify(location)}`,
      false,
    );
    return "Unspotted";
  }
};

/**
 * When {@link TrackCar#location} is loaded and its `id` matches {@link TrackCar#trackId} (spot aligned to graph).
 */
export const getAlignedTrackCarLocation = (trackCar) => {
  if (!trackCar?.trackId || !trackCar.location?.id) return null;
  return trackCar.location.id === trackCar.trackId ? trackCar.location : null;
};

/**
 * Display label for a shipment rail location from TrackCar only (aligned Location or locationFullName).
 * Does not fall back to inventory or legacy shipment fields — callers show NOT SPOTTED when null.
 */
export const getTrackCarLocationDisplayLabel = (trackCar) => {
  if (!trackCar) return null;
  const alignedLocation = getAlignedTrackCarLocation(trackCar);
  if (alignedLocation) {
    return getLocationFullName(alignedLocation);
  }
  const fullName =
    typeof trackCar.locationFullName === "string"
      ? trackCar.locationFullName.trim()
      : "";
  if (fullName) {
    return getLocationFullName(fullName);
  }
  return null;
};

export const isBulkArea = (area) => {
  let areaType = null;

  if (typeof area === "object") areaType = area?.areaType;
  else if (typeof area === "string") {
    const newArea = getArea(area);
    areaType = newArea?.areaType;
  }
  return areaType === AreaType.BULK;
};

export const isBulkLocation = (location) => {
  if (!location) return false;

  if (!location?.type) {
    const hasLocationData =
      typeof location === "object" && Object.keys(location).length > 0;
    if (hasLocationData) {
      logError(
        "isBulkLocation: location.type is undefined",
        { location },
        null,
      );
    }
    return false;
  }
  return (
    location.type === LocationType.BULK ||
    location.type === LocationType.BULK_MOBILE ||
    hasBulkLoadType(location)
  );
};

export const hasBulkLoadType = (location) => {
  if (!location?.locationLoad) return false;
  return (
    location.locationLoad[LocationLoadType.DRY_BULK]?.selected ||
    location.locationLoad[LocationLoadType.LIQUID_BULK]?.selected
  );
};

export const isStructuredLocation = (location) => {
  return location.type === LocationType.STRUCTURED;
};

export const filterAreas = (areas, yardCommodity, execludeAreaType) => {
  if (!yardCommodity) return areas;

  let commodity = yardCommodity?.commodity;
  if (!commodity) {
    yardCommodity = getYardCommodity(yardCommodity);
    commodity = yardCommodity.commodity;
  }

  if (commodity && isBulkCommodity(commodity)) {
    return areas.filter((area) => {
      const isActive = area.active === "true";
      const isBulk =
        area.areaType === AreaType.TRACK || area.areaType === AreaType.BULK;
      const isNotExecluded = execludeAreaType
        ? area.areaType !== execludeAreaType
        : true;
      return isActive && isBulk && isNotExecluded;
    });
  }

  // other load types go to STRUCTURED
  return areas.filter((area) => {
    const isActive = area.active === "true";
    const isNotBulk = area.areaType !== AreaType.BULK;
    const isNotExecluded = execludeAreaType
      ? area.areaType !== execludeAreaType
      : true;
    return isActive && isNotBulk && isNotExecluded;
  });
};

export const filterZones = (zones, yardCommodityId, accountId) => {
  return zones.filter((zone) => {
    const isActive = zone.active === "true";
    const matchesYardCommodity = yardCommodityId
      ? zone.reloadYardCommodityId === yardCommodityId ||
        !zone.reloadYardCommodityId
      : true;
    const matchesAccount = accountId
      ? zone.accountId === accountId || !zone.accountId
      : true;

    return isActive && matchesYardCommodity && matchesAccount;
  });
};

export const filterAisles = (aisles, inventory) => {
  if (!aisles || aisles?.length === 0) return aisles;
  const isContainerDrop = !!inventory.containerNumber;

  const isBulk = isBulkShipment(
    inventory.reloadYardCommodity?.commodity,
    inventory.area,
  );

  if (isBulk && isContainerDrop) {
    return aisles.filter((aisle) => aisle.locationLoad?.container.selected);
  }

  // CONTAINER can only by stored in CONTAINER aisle
  if (isShippingContainer(inventory?.reloadYardCommodity?.commodity)) {
    return aisles.filter((aisle) => aisle.locationLoad?.container.selected);
  }

  // inventoryLoadType.BULK can only be stored in BULK aisles already filtered by filterZones
  // mark NOT_AVAILABLE if full
  let area = inventory?.area;
  if (!area) area = aisles[0].area;

  // inventoryLoadType.OTHER can only be stored in UNSTRUCTURED and STRUCTURED.OTHER
  if (
    inventory?.reloadYardCommodity?.commodity?.locationLoadType ===
    LocationLoadType.OTHER
  ) {
    return aisles.filter(
      (aisle) =>
        aisle.type === LocationType.UNSTRUCTURED ||
        aisle.locationLoad?.other.selected,
    );
  }

  // if load type box, pallet, or supersack, then go to any STRUCTURED, including other
  // bulk already filtered at filterZones
  return aisles.filter((aisle) => !aisle.locationLoad?.container.selected);
};

export const isFloorLoaded = (aisleOrLocation) => {
  // An item whose locationId never resolved to a Location row -- the
  // UNSPOTTED_VIRTUAL sentinel the handleTrackCarChange lambda writes when a
  // railcar is un-spotted -- has nothing stacked above it, because it is not
  // anywhere. Nothing can block its relocate.
  if (!aisleOrLocation) return false;
  return (
    aisleOrLocation.topDownPickOrder ||
    aisleOrLocation.aisle?.topDownPickOrder ||
    aisleOrLocation.locationLoad?.container?.selected
  );
};

export const isUnmanagedAisle = (aisle) => {
  if (!aisle) return true;
  if (isBulkLocation(aisle)) return false;
  return (
    aisle.type === LocationType.UNSTRUCTURED ||
    aisle.locationLoad?.other.selected
  );
};

export const isUnmanagedLocation = (location) => {
  return isUnmanagedAisle(location);
};

export const isManagedLocation = (location) => {
  return !isUnmanagedAisle(location);
};

export const isBulkLocationAvailable = (
  toLocation,
  currentInventory,
  qtyToPlace,
  isDamagedLoad = false,
) => {
  // STRUCTURED BULK
  if (!toLocation) return false;
  if (toLocation.type !== LocationType.BULK) return false;

  // cannot place two different products in the same location
  if (toLocation.productId !== currentInventory.productId) return false;

  // cannot mix conditions in one tank: clean stock never enters a
  // contaminated tank, damaged stock never enters a clean, occupied one
  if (!canStoreInBulkTank(toLocation, isDamagedLoad)) return false;

  // cannot place if location is full
  if (
    (toLocation.capacityUsed || 0) >= toLocation.bulkCapacity ||
    (toLocation.capacityAllocated || 0) >= toLocation.bulkCapacity
  ) {
    return false;
  }

  // cannot place if required space too small to accept qty
  const requiredSpace =
    (toLocation.capacityUsed || 0) +
    (toLocation.capacityAllocated || 0) +
    qtyToPlace;
  if (requiredSpace > toLocation.bulkCapacity) return false;

  return true;
};

export const isLocationAvailable = (bayList, toLocation, currentInventory) => {
  try {
    // BULK locationes are filtered out by filterAreas, filterZones and filterAisles

    // UNSTRUCTURED
    if (!toLocation) return false;
    if (toLocation.type === LocationType.UNSTRUCTURED) return true;

    // STRUCTURED
    if (bayList?.length === 0) return false;

    // cannot place if location is full
    if (
      (toLocation.capacityUsed || 0) >= 100 ||
      (toLocation.capacityAllocated || 0) >= 100
    )
      return false;

    const inventoryLoadType =
      currentInventory.reloadYardCommodity.commodity.locationLoadType;
    const toLocationLoadType = toLocation.locationLoad[inventoryLoadType];

    // can place any loadType to OTHER except for CONTAINER. OTHER can take any mix of products.
    if (
      toLocation.locationLoad.other.selected &&
      inventoryLoadType !== LocationLoadType.CONTAINER
    ) {
      if (isFloorLoaded(toLocation)) {
        return checkFloorLoaded(bayList, toLocation);
      }
      return true;
    }

    // cannot place two different products in the same location
    if (
      toLocation.productId &&
      toLocation.productId !== currentInventory.productId
    )
      return false;

    const isContainer =
      !!currentInventory.containerNumber?.trim() &&
      toLocation.type === LocationType.BULK_MOBILE;

    // cannot place if location does not accept this LoadType. inventory.loadType !== location.loadType
    if (!toLocationLoadType.selected && !isContainer) return false;

    // cannot place if required space too small to accept qty
    const requiredSpace =
      (toLocation.capacityUsed || 0) +
      (toLocation.capacityAllocated || 0) +
      toLocationLoadType.capacity;
    if (requiredSpace > 100) return false;

    // FLOOR-LOADED & CONTAINER
    if (isFloorLoaded(toLocation)) {
      return checkFloorLoaded(bayList, toLocation);
    }
  } catch (error) {
    logError("locationUtils:isLocationAvailable", error, false);
    logError("locationUtils:isLocationAvailable:data", currentInventory, false);
  }

  return true;
};

const checkFloorLoaded = (bayList, toLocation) => {
  // can place on the floor
  if (toLocation.level === 1) return true;

  // cannot place if space above would bave more than space below
  const locationBay = bayList[toLocation.bay - 1];
  const locationBelow =
    locationBay[toLocation.level - 2][toLocation.position - 1];

  const quantity =
    (toLocation.capacityUsed || 0) + (toLocation.capacityAllocated || 0);
  const quantityBelow =
    (locationBelow.capacityUsed || 0) + (locationBelow.capacityAllocated || 0);
  if (quantityBelow <= quantity) return false;

  return true;
};

export const canRelocate = (
  bayList,
  toLocation,
  inventoryItem,
  currentInventory,
  disableRelocateValidation,
  inventoryItemBayList,
) => {
  if (!inventoryItem) return true;

  const fromValidation = canRelocateFrom(
    inventoryItem,
    disableRelocateValidation,
    inventoryItemBayList,
  );
  if (!fromValidation) return false;
  if (!toLocation) return fromValidation;

  if (toLocation.type === LocationType.UNSTRUCTURED) {
    // can't relocate feom one aisle to the same aisle
    if (toLocation.aisleId === inventoryItem.aisleId) return false;
    return true;
  }

  // can't relocate items to the same location -- an item with no location at
  // all is nowhere, so it can never be the location the crew picked
  if (
    toLocation &&
    inventoryItem &&
    toLocation.id === inventoryItem.location?.id
  ) {
    return false;
  }

  // TODO: decide how to skip validation
  if (disableRelocateValidation) {
    return true;
  }

  // A bulk tank holds one pool in one condition. Without this, a refusal from
  // canMergeInventoryItems falls through to the move/create path and the
  // damaged row lands in the clean tank as a coexisting row -- which for an
  // indivisible commodity is the contamination event itself.
  if (
    toLocation.type === LocationType.BULK &&
    !canStoreInBulkTank(toLocation, inventoryItem.isDamaged)
  )
    return false;

  // can't relocate to a full location
  if (
    (toLocation.capacityUsed || 0) >= 100 ||
    (toLocation.capacityAllocated || 0) >= 100
  )
    return false;

  const inventoryLoadType =
    currentInventory.reloadYardCommodity.commodity.locationLoadType;
  const toLocationLoadType = toLocation.locationLoad[inventoryLoadType];

  // can relocate any load type to OTHER except CONTAINER. OTHER can take any mix of products.
  if (
    toLocation.locationLoad.other.selected &&
    inventoryLoadType !== LocationLoadType.CONTAINER
  )
    return true;

  // cannot place two different products in the same location
  if (
    toLocation.productId &&
    toLocation.productId !== currentInventory.productId
  )
    return false;

  // can't relocate to a location with a different load type
  if (!toLocationLoadType.selected) return false;

  // can't relocate to a location that doesn't have enough space
  const requiredSpace =
    (toLocation.capacityUsed || 0) +
    (toLocation.capacityAllocated || 0) +
    toLocationLoadType.capacity;
  if (requiredSpace > 100) return false;

  // floor loaded to location validationn
  if (isFloorLoaded(toLocation)) {
    // can't relocate above a location that has less capacity used
    const toLocationBay = bayList[toLocation.bay - 1];
    if (
      toLocation.level > 1 &&
      toLocationBay[toLocation.level - 1].length ===
        toLocationBay[toLocation.level - 2].length
    ) {
      const locationBelow =
        toLocationBay[toLocation.level - 2][toLocation.position - 1];
      if (locationBelow.capacityUsed < requiredSpace) {
        return false;
      }
    }
    // can't relocate items to a location above itself
    const fromLocation = inventoryItem.location;
    if (
      toLocation.aisle.id === fromLocation.aisle.id &&
      toLocation.bay === fromLocation.bay &&
      toLocation.level >= fromLocation.level &&
      toLocation.position === fromLocation.position
    ) {
      return false;
    }
  }

  return true;
};

export const canRelocateFrom = (
  inventoryItem,
  disableRelocateValidation,
  inventoryItemBayList,
) => {
  if (!inventoryItemBayList || !inventoryItem) return true;
  // TODO: decide how to skip validation
  if (disableRelocateValidation) {
    return true;
  }
  if (inventoryItem.quantityOnHand === 0) return false;
  const fromLocation = inventoryItem.location;

  // floor loaded from Location validation
  if (isFloorLoaded(inventoryItem.location)) {
    // can't relocate items that have other items above it
    const inventoryItemBay = inventoryItemBayList[fromLocation.bay - 1];
    if (inventoryItemBay && fromLocation.level < inventoryItemBay.length) {
      const levelAbove = inventoryItemBay[fromLocation.level];
      const locationAbove = levelAbove[fromLocation.position - 1];
      if (locationAbove.capacityUsed) return false;
    }
  }

  return true;
};

export const isRailShipmentInTrackArea = (areaOrAreaId, inboundShipment) => {
  return (
    isTrack(areaOrAreaId) && inboundShipment?.carrierType === CarrierType.RAIL
  );
};

export const isTruckToRail = (areaOrAreaId, inboundShipment) => {
  return (
    isTrack(areaOrAreaId) && inboundShipment?.carrierType === CarrierType.TRUCK
  );
};

export const hasTracks = () => {
  const hasTracks = window.appUser?.reloadYard?.aisles?.items?.some((aisle) =>
    isTrack(aisle.area),
  );

  return !!hasTracks;
};

export const getTrackArea = (reloadYard = window.appUser?.reloadYard) => {
  if (!reloadYard) return null;

  if (isTrack(reloadYard.trackArea)) {
    return reloadYard.trackArea;
  }

  const trackArea =
    reloadYard.areas?.items?.find((area) => isTrack(area)) || null;

  if (trackArea) {
    reloadYard.trackArea = trackArea;
  }

  return trackArea;
};

export const isTrack = (area) => {
  if (typeof area === "object") return area?.areaType === AreaType.TRACK;
  else if (typeof area === "string")
    return window.appUser.reloadYard.areaMap[area]?.areaType === AreaType.TRACK;
  return false;
};

/**
 * True if value is a valid track length (finite number >= 1). Used for validation and backfill.
 * @param {unknown} val - Value to check (string from input, number, null, undefined).
 * @returns {boolean} True if val is present, not empty string, and a finite number >= 1.
 */
export const isValidTrackLength = (val) => {
  const n = Number(val);
  return val != null && val !== "" && Number.isFinite(n) && n >= 1;
};

export const getArea = (areaId) => {
  // avoid using since appUser data may be stale after change by another user.
  return window.appUser.reloadYard?.areaMap[areaId];
};

export const getAreaName = (areaId) => {
  const area = getArea(areaId);
  return area?.name;
};

export const getZone = (area, zoneId) => {
  if (!zoneId?.trim()) return null;
  if (typeof area === "object" && area.zones) {
    return area.zones.items?.find((itm) => itm.id === zoneId);
  } else if (typeof area === "string") {
    const newArea = getArea(area);
    return newArea.zones.items?.find((itm) => itm.id === zoneId);
  }
  return null;
};

export const getZoneName = (areaId, zoneId) => {
  const zone = getZone(areaId, zoneId);
  return zone?.name;
};

export const sortByLocation = (inventoryItems, forPicking = false) => {
  return inventoryItems.sort((itemA, itemB) => {
    const locationA = itemA.location;
    const locationB = itemB.location;
    if (!locationA || !locationB) return 0;

    // Compare area sortKey
    if (locationA.area.sortKey !== locationB.area.sortKey) {
      return locationA.area.sortKey - locationB.area.sortKey;
    }
    // Compare zone sortKey
    if (locationA.zone.sortKey !== locationB.zone.sortKey) {
      return locationA.zone.sortKey - locationB.zone.sortKey;
    }
    // Compare aisle sortKey - stop sort for UNSTRUCTURED
    if (locationA.aisle.sortKey !== locationB.aisle.sortKey) {
      return locationA.aisle.sortKey - locationB.aisle.sortKey;
    }

    // Additional sorting by name for STRUCTURED locations
    if (
      locationA.type === LocationType.STRUCTURED &&
      locationB.type === LocationType.STRUCTURED
    ) {
      if (locationA.bay !== locationB.bay) return locationA.bay - locationB.bay;
      if (locationA.position !== locationB.position)
        return locationA.position - locationB.position;
      if (
        locationA.aisle.topDownPickOrder &&
        locationB.aisle.topDownPickOrder &&
        forPicking
      )
        return locationB.level - locationA.level;
      if (locationA.level !== locationB.level)
        return locationA.level - locationB.level;
    }

    // If both locations are UNSTRUCTURED and all sortKeys are equal, maintain original order
    return 0;
  });
};

export const sortShipmentItemByLocation = (
  shipmentlineItems,
  forPicking = false,
) => {
  return shipmentlineItems.sort((itemA, itemB) => {
    const locationA = itemA.inventoryItem.location;
    const locationB = itemB.inventoryItem.location;

    // Compare area sortKey
    if (locationA.area.sortKey !== locationB.area.sortKey) {
      return locationA.area.sortKey - locationB.area.sortKey;
    }
    // Compare zone sortKey
    if (locationA.zone.sortKey !== locationB.zone.sortKey) {
      return locationA.zone.sortKey - locationB.zone.sortKey;
    }
    // Compare aisle sortKey - stop sort for UNSTRUCTURED
    if (locationA.aisle.sortKey !== locationB.aisle.sortKey) {
      return locationA.aisle.sortKey - locationB.aisle.sortKey;
    }

    // Additional sorting by name for STRUCTURED locations
    if (
      locationA.type === LocationType.STRUCTURED &&
      locationB.type === LocationType.STRUCTURED
    ) {
      if (locationA.bay !== locationB.bay) return locationA.bay - locationB.bay;
      if (locationA.position !== locationB.position)
        return locationA.position - locationB.position;
      if (
        locationA.aisle.topDownPickOrder &&
        locationB.aisle.topDownPickOrder &&
        forPicking
      )
        return locationB.level - locationA.level;
      if (locationA.level !== locationB.level)
        return locationA.level - locationB.level;
    }

    // If both locations are UNSTRUCTURED and all sortKeys are equal, maintain original order
    return 0;
  });
};

const saveInventory = (toUpdateInventory) => {
  toUpdateInventory.locationId = null;
  return performUpdateInventoryMini(toUpdateInventory);
};

const saveInventoryItem = (inventoryItem) => {
  const toUpdateRecord = { ...inventoryItem };

  delete toUpdateRecord.aisle;
  delete toUpdateRecord.area;
  delete toUpdateRecord.createdAt;
  delete toUpdateRecord.inventory;
  delete toUpdateRecord.location;
  delete toUpdateRecord.seals;
  delete toUpdateRecord.updatedAt;
  delete toUpdateRecord.zone;

  return API.graphql(
    graphqlOperation(updateInventoryItemMini, { input: toUpdateRecord }),
  );
};

export const hasTrackCarsOnLocations = async (trackIds = []) => {
  const uniqueIds = Array.from(new Set(trackIds.filter(Boolean)));
  if (!uniqueIds.length) return false;

  try {
    const results = await Promise.all(
      uniqueIds.map(async (trackId) => {
        const response = await API.graphql(
          graphqlOperation(trackCarByActiveByTrackBySortKey, {
            reloadYardId: window.appUser.reloadYardId,
            activeTrackIdSortKey: { beginsWith: { active: "true", trackId } },
            limit: 1,
          }),
        );
        const items =
          response?.data?.trackCarByActiveByTrackBySortKey?.items || [];
        return items.length > 0;
      }),
    );
    return results.some(Boolean);
  } catch (err) {
    logError("locationUtils:hasTrackCarsOnLocations", err);
    return true;
  }
};

const findExistingBulkMobileAggregateAtLocation = async (location) => {
  if (location.type !== LocationType.BULK_MOBILE || !location?.id) {
    return null;
  }
  try {
    const result = await API.graphql(
      graphqlOperation(inventoryItemByLocationByStatusLookup, {
        locationId: location.id,
        status: { eq: InventoryStatus.STORED },
        limit: 25,
      }),
    );
    const items = result.data?.inventoryItemByLocationByStatus?.items ?? [];
    for (const item of items) {
      if (!item?.inventoryId) continue;
      const invResult = await API.graphql(
        graphqlOperation(getInventorie, { id: item.inventoryId }),
      );
      const inv = invResult.data?.getInventorie;
      if (
        inv?.productId === location.productId &&
        inv?.status === InventoryStatus.STORED
      ) {
        return { inventory: inv, inventoryItem: item };
      }
    }
  } catch (err) {
    logError("locationUtils:findExistingBulkMobileAggregateAtLocation", err);
  }
  return null;
};

export const createAggregateInventory = async (
  location,
  account,
  lotType,
  seals = [],
  aggregateKeyContext = {},
) => {
  const { shipmentInventory, inboundShipment } = aggregateKeyContext;
  const aggregatePoLot = (
    shipmentInventory?.containerNumber ||
    inboundShipment?.containerNumber ||
    ""
  ).trim();
  const createDateTime = new Date();
  const historyNote = {
    type: NoteType.INVENTORY,
    date: createDateTime,
    by: window.appUser?.fullName,
    text: `Aggregate created`,
  };

  const productResponse = await API.graphql(
    graphqlOperation(getLocationProduct, { id: location.productId }),
  );
  const product = productResponse.data.getProduct;

  if (location.type === LocationType.BULK_MOBILE && location.id) {
    const slotAggregate =
      await findExistingBulkMobileAggregateAtLocation(location);
    if (slotAggregate) {
      return {
        inbound: null,
        inventory: slotAggregate.inventory,
        inventoryItem: slotAggregate.inventoryItem,
      };
    }
  }

  if (location.type !== LocationType.BULK_MOBILE) {
    const inventoryResponse = await API.graphql(
      graphqlOperation(inventoryByAreaByProductByStatus, {
        areaId: location.areaId,
        productIdStatus: {
          beginsWith: {
            productId: location.productId,
            status: InventoryStatus.STORED,
          },
        },
        limit: 1,
      }),
    );
    const existingInventory =
      inventoryResponse.data.inventoryByAreaByProductByStatus.items[0];

    if (existingInventory) {
      const promiseList = [];

      promiseList.push(
        createAggregateInventoryItem(
          location,
          existingInventory.inboundShipmentId,
          existingInventory.id,
          existingInventory.accountId,
          historyNote,
          seals,
        ),
      );

      if (!existingInventory.aisleList.includes(location.aisleId)) {
        existingInventory.aisleList.push(location.aisleId);
      }
      promiseList.push(saveInventory(existingInventory));

      const [inventoryItem, updatedInventory] = await Promise.all(promiseList);

      return {
        inbound: null,
        inventory: updatedInventory.data.updateInventorie,
        inventoryItem,
      };
    }
  }

  const getWeight = () => {
    const weightUOM = getWeightUOM(account, { product });
    if (weightUOM && product) {
      const weight = product[weightUOM.toLowerCase()];
      return weight;
    }
    return undefined;
  };

  const inboundCreateInput = {
    active: "true",
    groups: location.groups,
    status: InboundStatus.AGGREGATED,
    state: InboundState.COMPLETED,
    accountName: account.businessName,
    accountId: account.id,
    billToAccountId: account.defaultBillToAccountId || account.id,
    shipFrom: account.businessName,
    poNumber: aggregatePoLot,
    normalizedPoNumber: normalizeValue(aggregatePoLot),
    lotType: lotType || account.lotType || LotType.PERPETUAL,
    lotNumber: aggregatePoLot,
    reloadYardCommodityId: location.zone.reloadYardCommodityId,
    carrierType: CarrierType.TRUCK,
    carrierName: "",
    railCarNumber: "NA",
    scheduledDateTime: null,
    createDateTime: createDateTime,
    notifyDateTime: createDateTime,
    placedDateTime: createDateTime,
    releaseDateTime: createDateTime,
    totalUOM: location.bulkUnits,
    weightUnits: getWeightUOM(account, { product }),
    reloadYardId: location.reloadYardId,
    areaId: location.areaId,
    zoneId: location.zoneId,
    history: [historyNote],
  };

  let result = await API.graphql(
    graphqlOperation(createInboundShipmentId, { input: inboundCreateInput }),
  );
  const inbound = result.data.createInboundShipment;

  const inventoryCreateInput = {
    active: "true",
    groups: location.groups,
    status: InventoryStatus.STORED,
    inboundShipmentId: inbound.id,
    poNumber: aggregatePoLot,
    lotNumber: aggregatePoLot,
    lotType: lotType || account.lotType || LotType.PERPETUAL,
    accountId: account.id,
    accountName: account.businessName,
    productName: location.productName,
    productId: location.productId,
    productCode: product?.productCode,
    areaId: location.areaId,
    zoneId: location.zoneId,
    aisleList: [location.aisleId],
    quantityAdvised: 0,
    quantityReceived: 0,
    quantityOnHand: 0,
    quantityPhysical: 0,
    quantityCommitted: 0,
    receivedAt: createDateTime,
    inventoryUOM: location.bulkUnits,
    weightUOM: getWeightUOM(account, { product }),
    weight: getWeight(),
    reloadYardCommodityId: location.zone.reloadYardCommodityId,
    reloadYardId: location.reloadYardId,
    history: [historyNote],
    sortKey: SORTKEY_BUFFER,
  };

  if (
    location.type === LocationType.BULK_MOBILE &&
    location.containerNumber?.trim()
  ) {
    inventoryCreateInput.containerNumber = location.containerNumber.trim();
  }

  result = await API.graphql(
    graphqlOperation(createInventorieId, { input: inventoryCreateInput }),
  );
  const inventory = result.data.createInventorie;

  const inventoryItem = await createAggregateInventoryItem(
    location,
    inbound.id,
    inventory.id,
    account.id,
    historyNote,
    seals,
  );
  return { inbound, inventory, inventoryItem };
};

/**
 * Crew inbound: after a shipment line is first placed as AGGREGATED on bulk / BULK_MOBILE,
 * create or extend synthetic aggregate inventory (same as manager layout flows), roll
 * quantities, and return the aggregate inventory id for aggregateInventoryId on the
 * shipment inventory record.
 */
const ensureBulkAggregateAfterInboundPlace = async ({
  aggregateLocation,
  account,
  lotType,
  quantityDelta,
  shipmentInventory,
  inboundShipment,
}) => {
  if (!aggregateLocation?.productId) {
    logError(
      "locationUtils:ensureBulkAggregateAfterInboundPlace:missingProductId",
      { aggregateLocation },
      null,
    );
    return null;
  }
  if (!account?.id || !account?.businessName) {
    logError(
      "locationUtils:ensureBulkAggregateAfterInboundPlace:missingAccount",
      { account },
      null,
    );
    return null;
  }
  const delta = Number(quantityDelta || 0);
  const { inventory, inventoryItem } = await createAggregateInventory(
    aggregateLocation,
    account,
    lotType,
    [],
    { shipmentInventory, inboundShipment },
  );
  if (!inventory?.id || !inventoryItem?.id) {
    logError(
      "locationUtils:ensureBulkAggregateAfterInboundPlace:noInventory",
      { inventory, inventoryItem },
      null,
    );
    return null;
  }

  const nextAggQty = Number(inventory.quantityOnHand || 0) + delta;
  const nextItemQty = Number(inventoryItem.quantityOnHand || 0) + delta;
  const updatedAt = new Date().toISOString();

  const toUpdateInventory = {
    ...inventory,
    quantityOnHand: nextAggQty,
    quantityPhysical: nextAggQty,
    quantityReceived: nextAggQty,
    updatedAt,
  };

  const toUpdateItem = {
    ...inventoryItem,
    quantityOnHand: nextItemQty,
    updatedAt,
  };

  await Promise.all([
    performUpdateInventoryMini(toUpdateInventory),
    saveInventoryItem(toUpdateItem),
  ]);

  return { aggregateInventoryId: inventory.id };
};

const inventoryHasContainerCommodity = (inventory) => {
  const lt = inventory?.reloadYardCommodity?.commodity?.locationLoadType;
  if (typeof lt !== "string") return false;
  return lt.toLowerCase() === LocationLoadType.CONTAINER;
};

/**
 * Per-slot qty for a BULK_MOBILE ISO line (one inventory item per line).
 * Uses quantityOnHand; if unset/zero, fall back to inventory received/advised.
 */
const resolveBulkMobileISOSlotQuantity = (inventoryItem, inventory) => {
  const onHand = Number(inventoryItem.quantityOnHand ?? 0);
  if (onHand > 0) return onHand;
  return (
    Number(inventory.quantityReceived ?? 0) ||
    Number(inventory.quantityAdvised ?? 0) ||
    0
  );
};

const resolveLocationAccountName = (inventory, account, shipment) => {
  const fromInventory = (inventory?.accountName || "").trim();
  if (fromInventory) return fromInventory;
  const fromAccount = (account?.businessName || "").trim();
  if (fromAccount) return fromAccount;
  return (shipment?.accountName || "").trim();
};

/**
 * Absolute capacity + bulk metadata for BULK_MOBILE ISO slots on inbound (map save + mark complete).
 * When the slot receives a full container load, set bulkCapacity to the delivered quantity so
 * manager UI shows 100% (capacityUsed / bulkCapacity).
 */
export const applyIsoTainerLocationUpdate = ({
  toLocation,
  inventory,
  shipment,
  account,
  inventoryItem,
  removeFlag,
}) => {
  if (removeFlag) {
    toLocation.capacityUsed = 0;
    toLocation.capacityAllocated = 0;
    toLocation.productId = null;
    toLocation.productName = null;
    toLocation.accountName = null;
    toLocation.containerNumber = null;
    return toLocation;
  }
  const slotQty = resolveBulkMobileISOSlotQuantity(inventoryItem, inventory);
  const existingBulkCap = Number(toLocation.bulkCapacity || 0);
  const capacityAbsolute =
    existingBulkCap > 0 ? Math.min(slotQty, existingBulkCap) : slotQty;
  toLocation.capacityUsed = capacityAbsolute;
  toLocation.capacityAllocated = capacityAbsolute;
  if (slotQty > 0) {
    toLocation.bulkCapacity = existingBulkCap > 0 ? existingBulkCap : slotQty;
    const units =
      toLocation.bulkUnits ||
      inventory.inventoryUOM ||
      inventory.reloadYardCommodity?.units ||
      "";
    if (units) toLocation.bulkUnits = units;
  }
  toLocation.productId = inventory.productId;
  toLocation.productName = inventory.productName;
  toLocation.accountName =
    resolveLocationAccountName(inventory, account, shipment) || null;
  toLocation.containerNumber =
    slotQty > 0
      ? resolveInboundContainerNumber(inventory, shipment) || null
      : null;
  return toLocation;
};

/**
 * BULK_MOBILE ISO / container slot lines with a persisted location id.
 * Prefer `location.locationLoad.container.selected` on the slot; if that is missing on
 * the nested snapshot (common), fall back to container commodity on the inventory line.
 */
export const getISOtainerInventoryItems = (inventory) => {
  const items = inventory?.inventoryItems?.items || [];
  return items.filter((itm) => {
    if (!itm?.location?.id || itm.location.type !== LocationType.BULK_MOBILE) {
      return false;
    }
    if (itm.location.locationLoad?.container?.selected) return true;
    if (inventoryHasContainerCommodity(inventory)) return true;

    return false;
  });
};

/**
 * Inbound Mark Complete: for container-drop bulk lines on BULK_MOBILE ISO slots, persist
 * location capacity + product/account on each placed slot, then create/link synthetic STORED
 * aggregate inventory (same as manager layout). Idempotent when aggregateInventoryId
 * already set (map save): only refreshes location capacity/product.
 */
export const ensureBulkMobilePlacementAndAggregateOnInboundComplete = async ({
  shipment,
  inventory,
  account,
}) => {
  if (!shipment?.containerDrop || !isBulkArea(inventory?.areaId)) return null;

  const items = inventory.inventoryItems?.items || [];
  const isotainerInvItems = getISOtainerInventoryItems(inventory);
  if (isotainerInvItems.length === 0) return null;

  if (!account?.id || !account?.businessName) {
    logError(
      "locationUtils:ensureBulkMobilePlacementAndAggregateOnInboundComplete:missingAccount",
      { account },
      null,
    );
    return null;
  }
  if (!inventory.productId) {
    logError(
      "locationUtils:ensureBulkMobilePlacementAndAggregateOnInboundComplete:missingProductId",
      { inventoryId: inventory.id },
      null,
    );
    return null;
  }

  const seenLocationIds = new Set();
  const displayAccountName = resolveLocationAccountName(
    inventory,
    account,
    shipment,
  );
  const locationUpdatePromises = [];
  for (const itm of isotainerInvItems) {
    const locId = itm.location.id;
    if (seenLocationIds.has(locId)) continue;
    seenLocationIds.add(locId);
    const toLocation = { ...itm.location };
    applyIsoTainerLocationUpdate({
      toLocation,
      inventory,
      shipment,
      account,
      inventoryItem: itm,
      removeFlag: false,
    });

    locationUpdatePromises.push(promiseSetCapacityUsed(toLocation));
  }
  await Promise.all(locationUpdatePromises);

  if (inventory.aggregateInventoryId) {
    return { aggregateInventoryId: inventory.aggregateInventoryId };
  }

  const first = isotainerInvItems[0];
  const aggregateLocation = {
    ...first.location,
    productId: inventory.productId,
    productName: inventory.productName,
    accountName:
      displayAccountName || inventory.accountName || account.businessName || "",
    zone: {
      ...(first.location?.zone || {}),
      reloadYardCommodityId:
        first.location?.zone?.reloadYardCommodityId ||
        inventory.reloadYardCommodityId,
    },
    bulkUnits: first.location?.bulkUnits || inventory.inventoryUOM,
  };

  const sumOnHand = items.reduce(
    (sum, lineItem) => sum + Number(lineItem.quantityOnHand || 0),
    0,
  );
  const quantityDelta =
    sumOnHand > 0
      ? sumOnHand
      : Number(inventory.quantityReceived || 0) ||
        Number(inventory.quantityAdvised || 0) ||
        0;

  return ensureBulkAggregateAfterInboundPlace({
    aggregateLocation,
    account,
    lotType: inventory.lotType,
    quantityDelta,
    shipmentInventory: inventory,
    inboundShipment: shipment,
  });
};

const copySealsToInventory = async ({
  barcode,
  description,
  referenceId,
  reloadYardId,
  accountId,
  owner,
  validated,
  isScanned,
  createdBy,
}) => {
  const sealInput = {
    barcode,
    description,
    referenceId,
    referenceName: "InventoryItem",
    reloadYardId,
    accountId,
    owner,
    groups: window.appUser.groups,
    validated,
    active: true,
    isScanned,
    createdBy,
  };

  const res = await API.graphql(
    graphqlOperation(createSeal, { input: sealInput }),
  );

  return res.data?.createSeal;
};

const createAggregateInventoryItem = async (
  location,
  inboundShipmentId,
  inventoryId,
  accountId,
  historyNote,
  seals = [],
) => {
  const inventoryItemInput = {
    groups: location.groups,
    status: InventoryStatus.STORED,
    quantityOnHand: 0,
    quantityCommitted: 0,
    inventoryId,
    inboundShipmentId,
    reloadYardId: location.reloadYardId,
    accountId,
    areaId: location.areaId,
    zoneId: location.zoneId,
    aisleId: location.aisleId,
    locationId: location.id,
    locationFullName: getLocationFullName(location),
    history: [historyNote],
  };

  const result = await API.graphql(
    graphqlOperation(createInventoryItemId, { input: inventoryItemInput }),
  );
  const inventoryItemId = result.data.createInventoryItem.id;

  await Promise.all(
    seals.map((seal) =>
      copySealsToInventory({ ...seal, referenceId: inventoryItemId }),
    ),
  );

  return {
    ...inventoryItemInput,
    id: inventoryItemId,
  };
};

export const deAggregateInventory = async (location) => {
  const response = await API.graphql(
    graphqlOperation(inventoryByAreaByProductByStatusMax, {
      areaId: location.areaId,
      productIdStatus: {
        beginsWith: {
          productId: location.productId,
          status: InventoryStatus.STORED,
        },
      },
      limit: 1,
    }),
  );

  const existingInventory =
    response.data.inventoryByAreaByProductByStatus.items[0];
  if (existingInventory) {
    const promiseList = [];
    const { inboundShipment, inventoryItems } = existingInventory;
    inventoryItems.items = inventoryItems.items.filter(
      (item) => item.status !== InventoryStatus.ARCHIVED,
    );

    if (inventoryItems.items.length === 1) {
      // de-aggregate last location
      // also de-aggregate inventory & inbound
      const locationInventoryItem = inventoryItems.items.find(
        (itm) => itm.locationId === location.id,
      );
      if (locationInventoryItem) {
        locationInventoryItem.status = InventoryStatus.ARCHIVED;
        promiseList.push(saveInventoryItem(locationInventoryItem));
      }
      existingInventory.status = InventoryStatus.ARCHIVED;
      promiseList.push(saveInventory(existingInventory));

      inboundShipment.status = InboundStatus.ARCHIVED;
      promiseList.push(performUpdateInboundMini(inboundShipment));
    } else {
      // de-aggregate only one location
      const locationInventoryItem = inventoryItems.items.find(
        (itm) => itm.locationId === location.id,
      );
      if (locationInventoryItem) {
        // de-aggregate incentoryItem
        locationInventoryItem.status = InventoryStatus.ARCHIVED;
        promiseList.push(saveInventoryItem(locationInventoryItem));

        // update aisleList
        existingInventory.aisleList = existingInventory.aisleList.filter(
          (aisleId) => aisleId !== location.aisleId,
        );
        promiseList.push(saveInventory(existingInventory));
      }
    }
    return Promise.all(promiseList);
  } else {
    return Promise.resolve();
  }
};

export const doCreateLocation = async ({ location }) => {
  return API.graphql(graphqlOperation(createLocation, { input: location }));
};

export const doUpdateAisle = async ({ aisle }) => {
  const toUpdateAisle = { ...aisle };
  return API.graphql(graphqlOperation(updateAisle, { input: toUpdateAisle }));
};

export const doDeleteLocation = (location) => {
  const input = { id: location.id };
  return API.graphql(graphqlOperation(deleteLocation, { input }));
};

export const doUpdateLocation = async (selectedLocation) => {
  const toUpdateLocation = { ...selectedLocation };
  ["aisle", "area", "zone", "tags", "updatedAt", "createdAt"].forEach(
    (field) => {
      delete toUpdateLocation[field];
    },
  );
  await API.graphql(
    graphqlOperation(updateLocation, { input: toUpdateLocation }),
  );
};

export const resetMobileLocation = async ({ location }) => {
  try {
    const locationWithoutProduct = { ...location };
    if (location.productId) {
      locationWithoutProduct.productId = "";
      locationWithoutProduct.productName = "";
    }
    locationWithoutProduct.accountName = "";
    locationWithoutProduct.capacityUsed = 0;
    locationWithoutProduct.capacityAllocated = 0;
    locationWithoutProduct.active = "true";
    await doUpdateLocation(locationWithoutProduct);
  } catch (error) {
    logError(
      "src:utils:locationUtils:resetMobileLocation",
      `Error resetting mobile location: ${JSON.stringify(error)}`,
      false,
    );
    throw error;
  }
};

export const getLocationPrefix = ({ area, zone, name }) =>
  `${area.prefix}${zone.prefix}-${name}-`;

export const isLocationEditDisabled = (location) => {
  // if location is floor loaded, it is not editable
  if (isFloorLoaded(location)) return true;
  // if location had any capacityAllocated
  return location.capacityAllocated || location.capacityUsed;
};

export const getAisleBadgeContent = (aisle) => {
  return aisle.bays === 1 ? aisle.locations?.items?.length || "" : aisle.bays;
};

export const getSelectedLoadTypes = (locationLoad) => {
  let selectedTypes = locationLoad.other.selected ? " Other" : "";
  if (locationLoad.container.selected) selectedTypes += ", Container";
  if (locationLoad.pallet.selected) selectedTypes += ", Pallet";
  if (locationLoad.box.selected) selectedTypes += ", Box";
  if (locationLoad.superSack.selected) selectedTypes += ", Super Sack";
  if (locationLoad.dryBulk.selected) selectedTypes += ", Dry Bulk";
  if (locationLoad.liquidBulk.selected) selectedTypes += ", Liquid Sack";
  selectedTypes = selectedTypes.replace(/^,/, "");
  return selectedTypes;
};
